import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Mic } from 'lucide-react';

interface OnboardingOverlayProps {
  /** エントリ件数が0の初回ユーザーかどうか */
  isNewUser: boolean;
}

export default function OnboardingOverlay({ isNewUser }: OnboardingOverlayProps) {
  const [step, setStep] = useState<'welcome' | 'spotlight' | 'done'>(() => {
    if (localStorage.getItem('onboardingComplete') === 'true') return 'done';
    const saved = localStorage.getItem('onboardingStep');
    if (saved === 'dashboard') return 'spotlight';
    return 'welcome';
  });

  // 初回ユーザーでなければ表示しない
  useEffect(() => {
    if (!isNewUser) {
      setStep('done');
    }
  }, [isNewUser]);

  if (step === 'done') return null;
  if (localStorage.getItem('onboardingComplete') === 'true') return null;

  const handleStart = () => {
    setStep('spotlight');
    localStorage.setItem('onboardingStep', 'dashboard');
  };

  const handleSkip = () => {
    localStorage.setItem('onboardingComplete', 'true');
    localStorage.removeItem('onboardingStep');
    setStep('done');
  };

  return (
    <AnimatePresence>
      {step === 'welcome' && (
        <motion.div
          key="welcome"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-6"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.1, type: 'spring', stiffness: 300, damping: 25 }}
            className="bg-card rounded-2xl p-6 max-w-sm w-full shadow-xl space-y-4 text-center"
          >
            <div className="text-4xl">👋</div>
            <h2 className="text-xl font-bold">ひとりごとへようこそ！</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              声で話すだけで、AIが日記に仕上げてくれます。
              まずはマイクボタンを押して試してみましょう。
            </p>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" className="flex-1" onClick={handleSkip}>
                スキップ
              </Button>
              <Button className="flex-1" onClick={handleStart}>
                はじめる
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {step === 'spotlight' && (
        <motion.div
          key="spotlight"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] pointer-events-none"
        >
          {/* Dark overlay with hole for FAB */}
          <div className="absolute inset-0 bg-black/50 pointer-events-auto" onClick={handleSkip} />

          {/* Spotlight ring around FAB position */}
          <div className="absolute bottom-20 right-4 lg:bottom-6 z-[101]">
            <div className="relative">
              {/* Pulsing ring */}
              <motion.div
                animate={{ scale: [1, 1.3, 1], opacity: [0.5, 0, 0.5] }}
                transition={{ duration: 1.5, repeat: Infinity }}
                className="absolute -inset-3 rounded-full border-2 border-primary"
              />
              {/* FAB placeholder (clickthrough) */}
              <div className="h-14 w-14 rounded-full bg-primary flex items-center justify-center pointer-events-auto">
                <Mic className="h-6 w-6 text-primary-foreground" />
              </div>
            </div>
          </div>

          {/* Tooltip */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="absolute bottom-40 right-4 lg:bottom-24 z-[101] pointer-events-auto"
          >
            <div className="bg-card rounded-xl px-4 py-3 shadow-lg max-w-[260px]">
              <p className="text-sm font-medium">マイクボタンを押して</p>
              <p className="text-sm font-medium">今日の出来事を独り言にしてみよう</p>
              <div className="flex justify-end mt-2">
                <Button size="sm" variant="ghost" onClick={handleSkip}>
                  OK
                </Button>
              </div>
            </div>
            {/* Arrow */}
            <div className="absolute -bottom-2 right-8 w-4 h-4 bg-card rotate-45 shadow-lg" />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/** FABがクリックされた時に呼ぶ: 録音画面のオンボーディングに遷移 */
export function markOnboardingRecordStep() {
  if (localStorage.getItem('onboardingComplete') === 'true') return;
  localStorage.setItem('onboardingStep', 'record');
}
